import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Bell, ArrowRight, CheckCheck } from 'lucide-react';
import { notificationsApi } from '../../api/notifications';
import useInboxSSE from '../../hooks/useInboxSSE';
import useInboxStore from '../../stores/inboxStore';

const CATEGORY_DOT = {
  lead: 'bg-blue-500',
  alert: 'bg-red-500',
  approval: 'bg-amber-500',
  opportunity: 'bg-teal-500',
  info: 'bg-gray-400'
};

function relativeTime(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'jetzt';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function InboxBellDropdown() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setUnread = useInboxStore((s) => s.setUnreadCount);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // SSE temporaer deaktiviert: SSE-Stream blockierte HTTP/2-Connection-Pool
  // im Chrome, andere Requests (z.B. /lp/clusters) hingen dahinter.
  // Polling ueber refetchInterval reicht fuer Notifications.
  useInboxSSE({ enabled: false });

  // Unread-Count
  const { data: countData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => (await notificationsApi.unreadCount()).data,
    refetchInterval: 60_000
  });
  const unread = countData?.count || 0;

  // Kundenanfragen (leads) — immer getrennt anzeigen, damit sie nicht untergehen
  const { data: leadsData } = useQuery({
    queryKey: ['inbox', 'top-leads'],
    queryFn: async () => (await notificationsApi.listWithFilters({ status: 'unread', category: 'lead', limit: 5 })).data,
    enabled: open,
    staleTime: 30_000
  });
  const leadItems = leadsData?.items || [];

  // Top-5 unread ohne leads
  const { data: topData } = useQuery({
    queryKey: ['inbox', 'top5-nolead'],
    queryFn: async () => (await notificationsApi.listWithFilters({ status: 'unread', limit: 8 })).data,
    enabled: open,
    staleTime: 30_000
  });
  const otherItems = (topData?.items || []).filter((i) => i.category !== 'lead').slice(0, 5);

  useEffect(() => {
    setUnread(unread);
  }, [unread, setUnread]);

  // Click outside
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
    }
  });

  const openItem = (item) => {
    setOpen(false);
    if (item.link && item.link.startsWith('http')) {
      window.open(item.link, '_blank', 'noopener,noreferrer');
    } else if (item.link) {
      navigate(item.link);
    } else {
      navigate('/inbox');
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
        title="Inbox"
        aria-label="Inbox oeffnen"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none px-1">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-slate-700">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900 dark:text-slate-100">Inbox</span>
              {unread > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                  {unread} neu
                </span>
              )}
            </div>
            {unread > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                className="text-xs text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200 inline-flex items-center gap-1"
              >
                <CheckCheck size={12} /> Alle gelesen
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {leadItems.length === 0 && otherItems.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-slate-400">
                Keine ungelesenen Nachrichten.
              </div>
            ) : (
              <>
                {/* Kundenanfragen — eigene Sektion, damit sie nicht untergehen */}
                {leadItems.length > 0 && (
                  <div>
                    <div className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-800">
                      Kundenanfragen · {leadItems.length}
                    </div>
                    <ul className="divide-y divide-gray-100 dark:divide-slate-700">
                      {leadItems.map((item) => (
                        <li
                          key={item.id}
                          onClick={() => openItem(item)}
                          className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                        >
                          <div className="flex items-start gap-2">
                            <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${CATEGORY_DOT[item.category] || CATEGORY_DOT.info}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                                  {item.title}
                                </p>
                                <span className="ml-auto text-[10px] text-gray-400 dark:text-slate-500 shrink-0">
                                  {relativeTime(item.created_at)}
                                </span>
                              </div>
                              {item.message && (
                                <p className="text-xs text-gray-500 dark:text-slate-400 truncate mt-0.5">
                                  {item.message}
                                </p>
                              )}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Mitteilungen */}
                {otherItems.length > 0 && (
                  <div>
                    {leadItems.length > 0 && (
                      <div className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-slate-700">
                        Mitteilungen
                      </div>
                    )}
                    <ul className="divide-y divide-gray-100 dark:divide-slate-700">
                      {otherItems.map((item) => (
                        <li
                          key={item.id}
                          onClick={() => openItem(item)}
                          className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                        >
                          <div className="flex items-start gap-2">
                            <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${CATEGORY_DOT[item.category] || CATEGORY_DOT.info}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                                  {item.title}
                                </p>
                                <span className="ml-auto text-[10px] text-gray-400 dark:text-slate-500 shrink-0">
                                  {relativeTime(item.created_at)}
                                </span>
                              </div>
                              {item.message && (
                                <p className="text-xs text-gray-500 dark:text-slate-400 truncate mt-0.5">
                                  {item.message}
                                </p>
                              )}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>

          <button
            onClick={() => { setOpen(false); navigate('/inbox'); }}
            className="w-full px-4 py-3 text-sm font-medium text-orange-600 hover:bg-orange-50 dark:text-orange-300 dark:hover:bg-orange-900/20 border-t border-gray-200 dark:border-slate-700 flex items-center justify-center gap-1.5"
          >
            Alle anzeigen <ArrowRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
